import { isPast } from "date-fns";
import type React from "react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useLanguage } from "hooks/useSelectedLanguage";

import { StatusTag, type StatusTagProps } from "components/StatusTag/StatusTag";

import type { InviteBundleStatusTagProps } from "./InviteBundleStatusTag.model";

export const InviteBundleStatusTag: React.FC<InviteBundleStatusTagProps> = ({
  dataCy,
  maxUsage,
  currentUsage,
  expirationDate,
}) => {
  const { currentLanguage } = useLanguage();
  const { t } = useTranslation("teacher-dashboard");

  // Determine icon, color and label to display based on current status
  const bundleStatusTagProps: StatusTagProps = useMemo(() => {
    // Bundle will be considered full if all sent invites have been accepted
    if (maxUsage > 0 && currentUsage === maxUsage) {
      return {
        icon: "far fa-check-double",
        label: t("studentLicense.invitationBundleList.status.full"),
        variant: "default",
      };
    }

    // Bundle will be considered expired if it's license date is in the past
    if (isPast(new Date(expirationDate))) {
      return {
        icon: "fas fa-times-circle",
        label: t("studentLicense.invitationBundleList.status.expired"),
        variant: "error",
      };
    }

    // Bundle will be considered expired if associated license is active
    // and some students are yet to accept it
    return {
      icon: "far fa-check",
      label: t("studentLicense.invitationBundleList.status.active"),
      variant: "success",
    };
  }, [currentLanguage, currentUsage, maxUsage, expirationDate]);

  return (
    <StatusTag
      dataCy={dataCy}
      label={bundleStatusTagProps.label}
      icon={bundleStatusTagProps.icon}
      variant={bundleStatusTagProps.variant}
    />
  );
};
